/**
 * ProfileRequestsLens — PROFILE-REQ-01B
 * --------------------------------------
 * A LENS (not a page, not a menu, not a card) inside the existing
 * Coordinator Operations Workspace.  Reuses the same surface the
 * coordinator already uses for `Missing Documents` and `Missing
 * Consents` — same shell, same chrome, same shortcuts.
 *
 * Surfaces:
 *   • Pending / Completed / Overdue filter chips (reuse existing
 *     "BUCKET_CHIPS" visual idiom).
 *   • Table with Patient · Required fields · Requestor · Due · Status ·
 *     Completed rev# · Completed at.
 *   • Inline audit drawer (in-place expand) showing audit_trail[] events
 *     so coordinator can audit pending→completed lifecycle WITHOUT a new
 *     page.
 *
 * Data plane:
 *   GET /api/clinic/profile-requests?status={pending|completed|overdue|all}
 *   GET /api/clinic/profile-requests/{request_id}
 *
 * PROFILE-REQ-01 endpoints are unchanged.  Zero backend touch.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, AlertOctagon, ChevronDown, ChevronRight, User } from 'lucide-react';
import { api } from '../../utils/api';

const STATUS_META = {
  pending:   { key: 'pending',   tone: 'bg-amber-50 text-amber-700 border-amber-200',  icon: Clock },
  completed: { key: 'completed', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  overdue:   { key: 'overdue',   tone: 'bg-rose-50 text-rose-700 border-rose-200',     icon: AlertOctagon },
  cancelled: { key: 'cancelled', tone: 'bg-slate-50 text-slate-600 border-slate-200',  icon: Clock },
};

const CHIP_IDS = ['all', 'pending', 'overdue', 'completed'];

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return s; }
}

function fmtDateTime(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
}

export default function ProfileRequestsLens() {
  const { t } = useTranslation();
  const CHIP_LABEL_KEY = {
    all: 'provider.profileRequests.chipAll',
    pending: 'provider.profileRequests.chipPending',
    overdue: 'provider.profileRequests.chipOverdue',
    completed: 'provider.profileRequests.chipCompleted',
  };
  const [status, setStatus] = useState('pending');
  const [items, setItems]   = useState([]);
  const [buckets, setBuckets] = useState({ all: 0, pending: 0, overdue: 0, completed: 0, cancelled: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    api.get(`/clinic/profile-requests?status=${status}`)
      .then((r) => {
        if (!alive) return;
        setItems(r.data?.items || []);
        setBuckets(r.data?.buckets || {});
      })
      .catch((e) => { if (alive) setError(e?.response?.data?.message || e.message || t('provider.profileRequests.loadFailed')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [status, t]);

  const visible = items;

  return (
    <div className="space-y-4" data-testid="profile-requests-lens">
      {/* Filter chips — reuse the BUCKET_CHIPS visual idiom from Cases lens */}
      <div className="flex flex-wrap items-center gap-2" data-testid="profile-requests-filter-chips">
        {CHIP_IDS.map((id) => {
          const active = status === id;
          const cnt = id === 'all' ? buckets.all : buckets[id] ?? 0;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setStatus(id)}
              data-testid={`profile-requests-chip-${id}`}
              className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors ${
                active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
              }`}
            >
              {t(CHIP_LABEL_KEY[id])} <span className={`ml-1 ${active ? 'text-white/80' : 'text-slate-500'}`}>· {cnt}</span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="border border-rose-200 bg-rose-50 text-rose-700 rounded px-3 py-2 text-sm" data-testid="profile-requests-error">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-sm text-slate-500" data-testid="profile-requests-loading">{t('provider.profileRequests.loading')}</div>
      )}

      {!loading && visible.length === 0 && !error && (
        <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500" data-testid="profile-requests-empty">
          {status === 'all' ? t('provider.profileRequests.emptyAll') : t('provider.profileRequests.emptyStatus', { status: t(CHIP_LABEL_KEY[status] || 'provider.profileRequests.chipPending').toLowerCase() })}
        </div>
      )}

      {!loading && visible.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="profile-requests-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-slate-50 text-slate-700">
              <tr className="text-left">
                <th className="px-3 py-2 font-medium w-8" />
                <th className="px-3 py-2 font-medium">{t('provider.profileRequests.colPatient')}</th>
                <th className="px-3 py-2 font-medium">{t('provider.profileRequests.colRequiredFields')}</th>
                <th className="px-3 py-2 font-medium">{t('provider.profileRequests.colRequestor')}</th>
                <th className="px-3 py-2 font-medium">{t('provider.profileRequests.colDue')}</th>
                <th className="px-3 py-2 font-medium">{t('provider.profileRequests.colStatus')}</th>
                <th className="px-3 py-2 font-medium">{t('provider.profileRequests.colCompletedRev')}</th>
                <th className="px-3 py-2 font-medium">{t('provider.profileRequests.colCompletedAt')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((r) => {
                const metaKey = r.is_overdue && r.status === 'pending' ? 'overdue' : r.status;
                const meta = STATUS_META[metaKey] || STATUS_META.pending;
                const statusLabelKey = `provider.profileRequests.status${meta.key.charAt(0).toUpperCase()}${meta.key.slice(1)}`;
                const Icon = meta.icon;
                const isOpen = !!expanded[r.request_id];
                return (
                  <React.Fragment key={r.request_id}>
                    <tr
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => setExpanded((p) => ({ ...p, [r.request_id]: !p[r.request_id] }))}
                      data-testid={`profile-request-row-${r.request_id}`}
                    >
                      <td className="px-3 py-2 text-slate-400">
                        {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{r.patient_name || r.patient_id}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{(r.patient_id || '').slice(0, 8)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {(r.required_fields || []).map((f) => (
                            <span key={f} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[11px] font-mono">{f}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <User className="h-3 w-3 text-slate-400" />
                          <span className="text-slate-800">{r.requestor_name || '—'}</span>
                        </div>
                        <div className="text-[11px] text-slate-500">{r.requestor_role || ''}</div>
                      </td>
                      <td className="px-3 py-2">{fmtDate(r.due_at)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-medium ${meta.tone}`}>
                          <Icon className="h-3 w-3" />
                          {t(statusLabelKey)}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11.5px]">{r.completed_rev_no != null ? `#${r.completed_rev_no}` : '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{fmtDateTime(r.completed_at)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50" data-testid={`profile-request-detail-${r.request_id}`}>
                        <td />
                        <td colSpan={7} className="px-3 py-3">
                          <div className="text-[12px] text-slate-700 space-y-2">
                            <div>
                              <span className="font-medium">{t('provider.profileRequests.reason')}</span> {r.reason || '—'}
                            </div>
                            <div>
                              <span className="font-medium">{t('provider.profileRequests.requestedAt')}</span> {fmtDateTime(r.requested_at || r.created_at)}
                            </div>
                            <div className="border-t border-slate-200 pt-2">
                              <div className="font-medium mb-1">{t('provider.profileRequests.auditTrail')}</div>
                              <ol className="space-y-1 text-[11.5px]" data-testid={`profile-request-audit-trail-${r.request_id}`}>
                                {(r.audit_trail || []).map((e, i) => (
                                  <li key={i} className="flex items-center gap-2 font-mono">
                                    <span className="text-slate-500">{fmtDateTime(e.at)}</span>
                                    <span className="text-slate-800">{e.event}</span>
                                    <span className="text-slate-500">{t('provider.profileRequests.actor')}</span>
                                    <span className="text-slate-700">{(e.actor || '').slice(0, 8)}…</span>
                                    <span className="text-slate-500">({e.actor_role || '—'})</span>
                                    {e.completed_rev_no != null && (
                                      <span className="text-emerald-700">{t('provider.profileRequests.revision', { n: e.completed_rev_no })}</span>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
